import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import { AlertTriangle } from 'lucide-react';
import { Alert } from 'react-bootstrap';
import { useUsersData } from '../Helpers/UsersContext';
import { BaseType } from '../types/Enums';
import { REQUIRED_CONFIG_PATHS, ASSESSMENT_TEMPLATE_PATHS } from '../hooks/setup/useInstanceSetup';
import { buildingChecksApi } from '../api/buildingChecksApi';
import { componentFieldsApi } from '../api/componentFieldsApi';
import { missingDefaultTypes } from '../Helpers/requiredDefaultTypes';
import { useBuildComponentTypes } from '../contexts/BuildComponentTypesContext';
import { maintenanceLibrary } from '../library/maintenanceLibrary';
import { lifetimeLibrary } from '../library/lifetimeLibrary';
import { inspectionLibrary } from '../library/inspectionLibrary';
import { useBackendInfo } from '../hooks/superadmin/useBackendInfo';
import { isBackendOutdated, REQUIRED_BACKEND_LEVEL } from '../utils/backendLevel';

// BaseTypes that require a category field
const TYPES_REQUIRING_CATEGORY = [BaseType.Utility, BaseType.Outdoor];

/** Superadmin-managed template libraries that should be seeded on a new instance. */
const LIBRARY_CHECKS = [
    {
        key: 'maintenanceLibrary',
        path: maintenanceLibrary.path,
        settingsLink: '/superadmin/maintenance-library',
        message: 'Underhållsbiblioteket är tomt. Inga mallar för underhållsaktiviteter finns att kopiera.',
        linkText: 'Hantera underhållsbibliotek',
    },
    {
        key: 'lifetimeLibrary',
        path: lifetimeLibrary.path,
        settingsLink: '/superadmin/lifetime-library',
        message: 'Livslängdsbiblioteket är tomt. Inga mallar för livslängd och ersättningskostnad finns att kopiera.',
        linkText: 'Hantera livslängdsbibliotek',
    },
    {
        key: 'inspectionLibrary',
        path: inspectionLibrary.path,
        settingsLink: '/superadmin/inspection-library',
        message: 'Kontrollbiblioteket är tomt. Inga mallar för kontroller finns att kopiera.',
        linkText: 'Hantera kontrollbibliotek',
    },
] as const;

type LibraryKey = (typeof LIBRARY_CHECKS)[number]['key'];

const SetupWarningBanner: React.FC = () => {
    const location = useLocation();
    const auth = getAuth();
    const { users } = useUsersData();
    const { refresh: refreshComponentTypes } = useBuildComponentTypes();

    const [dismissedConfig, setDismissedConfig] = useState(false);
    const [dismissedEmail, setDismissedEmail] = useState(false);
    const [dismissedCategory, setDismissedCategory] = useState(false);
    const [dismissedWebAppUrl, setDismissedWebAppUrl] = useState(false);
    const [dismissedLibraries, setDismissedLibraries] = useState<Record<LibraryKey, boolean>>({
        maintenanceLibrary: false,
        lifetimeLibrary: false,
        inspectionLibrary: false,
    });
    const [missingRequired, setMissingRequired] = useState(false);
    const [missingWebAppUrl, setMissingWebAppUrl] = useState(false);
    const [missingLibraries, setMissingLibraries] = useState<Record<LibraryKey, boolean>>({
        maintenanceLibrary: false,
        lifetimeLibrary: false,
        inspectionLibrary: false,
    });
    const [typesMissingCategory, setTypesMissingCategory] = useState<string[]>([]);
    const [checked, setChecked] = useState(false);
    // Skickbedömning system templates: admin-level (not only superadmin), created inline (D28)
    const [missingAssessmentTemplates, setMissingAssessmentTemplates] = useState(false);
    const [dismissedAssessmentTemplates, setDismissedAssessmentTemplates] = useState(false);
    const [creatingAssessmentTemplates, setCreatingAssessmentTemplates] = useState(false);
    const [assessmentTemplatesError, setAssessmentTemplatesError] = useState<string | null>(null);
    // Component types iOS writes by fixed key (REQUIRED_DEFAULT_TYPES): admin-level, created inline like the templates
    const [missingDefaultTypeNames, setMissingDefaultTypeNames] = useState<string[]>([]);
    const [dismissedDefaultTypes, setDismissedDefaultTypes] = useState(false);
    const [creatingDefaultTypes, setCreatingDefaultTypes] = useState(false);
    const [defaultTypesError, setDefaultTypesError] = useState<string | null>(null);

    const currentUser = (() => {
        if (!auth.currentUser || !users) return null;
        return users.find(u => u.key === auth.currentUser?.uid) || null;
    })();

    const isAdmin = currentUser?.admin || currentUser?.superadmin || false;
    const isSuperadmin = currentUser?.superadmin || false;

    // Backend older than this web build needs (superadmin only); a failed read shows nothing
    const backend = useBackendInfo(isSuperadmin);
    const [dismissedBackend, setDismissedBackend] = useState(false);

    // Check for active users missing notification email
    const usersWithoutEmail = users?.filter(u => !u.deleted && !u.email) || [];
    const hasMissingEmails = usersWithoutEmail.length > 0;

    useEffect(() => {
        if (!isAdmin) return;

        const db = getDatabase();

        Promise.allSettled(
            REQUIRED_CONFIG_PATHS.map(path => get(ref(db, path)))
        ).then(results => {
            const anyMissing = results.some(
                r => r.status === 'rejected' || !r.value.exists()
            );
            setMissingRequired(anyMissing);

            // Check for component types missing category
            const componentTypesResult = results[REQUIRED_CONFIG_PATHS.indexOf('config/buildComponentTypes')];
            // A rejected read is treated as configured to avoid a false warning
            if (componentTypesResult?.status === 'fulfilled') {
                const typesNode = componentTypesResult.value.val() as Record<string, unknown> | null;
                setMissingDefaultTypeNames(missingDefaultTypes(typesNode).map(entry => entry.name));
            }
            if (componentTypesResult?.status === 'fulfilled' && componentTypesResult.value.exists()) {
                const typesData = componentTypesResult.value.val() as Record<string, { baseType?: string; category?: string; translations?: { sv?: string } }>;
                const missingCategories: string[] = [];

                for (const [typeKey, typeDef] of Object.entries(typesData)) {
                    if (typeDef.baseType && TYPES_REQUIRING_CATEGORY.includes(typeDef.baseType as BaseType)) {
                        if (!typeDef.category) {
                            // Use Swedish translation if available, otherwise the key
                            const displayName = typeDef.translations?.sv || typeKey;
                            missingCategories.push(displayName);
                        }
                    }
                }

                setTypesMissingCategory(missingCategories);
            }

            setChecked(true);
        });

        Promise.allSettled(
            ASSESSMENT_TEMPLATE_PATHS.map(path => get(ref(db, path)))
        ).then(results => {
            // A rejected read is treated as configured to avoid a false warning
            setMissingAssessmentTemplates(results.some(r => r.status === 'fulfilled' && !r.value.exists()));
        });
    }, [isAdmin]);

    const handleCreateAssessmentTemplates = async () => {
        setCreatingAssessmentTemplates(true);
        setAssessmentTemplatesError(null);
        try {
            await buildingChecksApi.ensureAssessmentTemplates();
            setMissingAssessmentTemplates(false);
        } catch (error) {
            console.error('Error creating assessment templates:', error);
            setAssessmentTemplatesError(error instanceof Error ? error.message : 'Kunde inte skapa mallarna');
        } finally {
            setCreatingAssessmentTemplates(false);
        }
    };

    const handleCreateDefaultTypes = async () => {
        setCreatingDefaultTypes(true);
        setDefaultTypesError(null);
        try {
            await componentFieldsApi.ensureDefaultTypes();
            setMissingDefaultTypeNames([]);
            refreshComponentTypes();
        } catch (error) {
            console.error('Error creating default types:', error);
            setDefaultTypesError(error instanceof Error ? error.message : 'Kunde inte skapa standardtyperna');
        } finally {
            setCreatingDefaultTypes(false);
        }
    };

    // Check for webAppUrl and template libraries (superadmin only)
    useEffect(() => {
        if (!isSuperadmin) return;

        const db = getDatabase();
        get(ref(db, 'global/webAppUrl')).then(snapshot => {
            setMissingWebAppUrl(!snapshot.exists() || !snapshot.val());
        });

        Promise.allSettled(
            LIBRARY_CHECKS.map(check => get(ref(db, check.path)))
        ).then(results => {
            const missing = { maintenanceLibrary: false, lifetimeLibrary: false, inspectionLibrary: false };
            results.forEach((result, i) => {
                // A rejected read is treated as configured to avoid a false warning
                missing[LIBRARY_CHECKS[i].key] = result.status === 'fulfilled' && !result.value.exists();
            });
            setMissingLibraries(missing);
        });
    }, [isSuperadmin]);

    if (!isAdmin) return null;

    const showConfigWarning = checked && missingRequired && !dismissedConfig && location.pathname !== '/setup';
    const showEmailWarning = hasMissingEmails && !dismissedEmail && !location.pathname.startsWith('/settings/manage-users');
    const showCategoryWarning = checked && typesMissingCategory.length > 0 && !dismissedCategory && location.pathname !== '/settings/component-types';
    const deployedBackend = backend.status === 'loaded' ? backend.info : null;
    const showBackendWarning = isSuperadmin
        && backend.status === 'loaded'
        && isBackendOutdated(deployedBackend)
        && !dismissedBackend
        && location.pathname !== '/superadmin/instance-settings';
    const showWebAppUrlWarning = isSuperadmin && missingWebAppUrl && !dismissedWebAppUrl && location.pathname !== '/superadmin/instance-settings';
    const visibleLibraryWarnings = LIBRARY_CHECKS.filter(check =>
        isSuperadmin
        && missingLibraries[check.key]
        && !dismissedLibraries[check.key]
        && location.pathname !== check.settingsLink
    );

    const showAssessmentTemplatesWarning = missingAssessmentTemplates
        && !dismissedAssessmentTemplates
        && location.pathname !== '/settings/component-types'
        && location.pathname !== '/setup';

    const showDefaultTypesWarning = missingDefaultTypeNames.length > 0
        && !dismissedDefaultTypes
        && location.pathname !== '/setup';

    if (!showBackendWarning && !showConfigWarning && !showEmailWarning && !showCategoryWarning && !showWebAppUrlWarning && !showAssessmentTemplatesWarning && !showDefaultTypesWarning && visibleLibraryWarnings.length === 0) return null;

    return (
        <>
            {showBackendWarning && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setDismissedBackend(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        Backend är inte uppdaterad för den här webbversionen (nivå{' '}
                        {deployedBackend ? deployedBackend.level : 'saknas'}, kräver {REQUIRED_BACKEND_LEVEL}).
                        Driftsätt mf-functions till instansen.{' '}
                        <Alert.Link as={Link} to="/superadmin/instance-settings">
                            Visa backendversion
                        </Alert.Link>
                    </span>
                </Alert>
            )}
            {showConfigWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedConfig(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    {isSuperadmin ? (
                        <span>
                            Obligatorisk konfiguration saknas.{' '}
                            <Alert.Link as={Link} to="/setup">
                                Visa konfigurationsstatus
                            </Alert.Link>
                        </span>
                    ) : (
                        <span>
                            Viss konfiguration saknas för instansen. Kontakta Molnfastigheters
                            support på{' '}
                            <Alert.Link href="mailto:support@molnfastigheter.se">
                                support@molnfastigheter.se
                            </Alert.Link>
                            .
                        </span>
                    )}
                </Alert>
            )}
            {showCategoryWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedCategory(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        {typesMissingCategory.length === 1
                            ? `1 komponenttyp saknar kategori: ${typesMissingCategory[0]}.`
                            : `${typesMissingCategory.length} komponenttyper saknar kategori.`}{' '}
                        <Alert.Link as={Link} to="/settings/component-types">
                            Hantera komponenttyper
                        </Alert.Link>
                    </span>
                </Alert>
            )}
            {showAssessmentTemplatesWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedAssessmentTemplates(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        Mallar för skickbedömning saknas.{' '}
                        {creatingAssessmentTemplates ? (
                            <span className="text-muted">Skapar mallar...</span>
                        ) : (
                            <Alert.Link onClick={handleCreateAssessmentTemplates}>
                                Skapa mallar
                            </Alert.Link>
                        )}
                        {assessmentTemplatesError && (
                            <span className="text-danger ms-2">{assessmentTemplatesError}</span>
                        )}
                    </span>
                </Alert>
            )}
            {showDefaultTypesWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedDefaultTypes(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        {missingDefaultTypeNames.length === 1
                            ? `Standardtypen ${missingDefaultTypeNames[0]} saknas bland komponenttyperna.`
                            : `${missingDefaultTypeNames.length} standardtyper saknas bland komponenttyperna.`}{' '}
                        {creatingDefaultTypes ? (
                            <span className="text-muted">Skapar...</span>
                        ) : (
                            <Alert.Link onClick={handleCreateDefaultTypes}>
                                Skapa
                            </Alert.Link>
                        )}
                        {defaultTypesError && (
                            <span className="text-danger ms-2">{defaultTypesError}</span>
                        )}
                    </span>
                </Alert>
            )}
            {showEmailWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedEmail(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        {usersWithoutEmail.length === 1
                            ? '1 användare saknar e-postadress för aviseringar.'
                            : `${usersWithoutEmail.length} användare saknar e-postadress för aviseringar.`}{' '}
                        <Alert.Link as={Link} to="/settings/manage-users">
                            Hantera användare
                        </Alert.Link>
                    </span>
                </Alert>
            )}
            {showWebAppUrlWarning && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedWebAppUrl(true)}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        Webb-URL för instansen är inte konfigurerad. Gästlänkar fungerar inte korrekt.{' '}
                        <Alert.Link as={Link} to="/superadmin/instance-settings">
                            Konfigurera instansinställningar
                        </Alert.Link>
                    </span>
                </Alert>
            )}
            {visibleLibraryWarnings.map(check => (
                <Alert
                    key={check.key}
                    variant="warning"
                    dismissible
                    onClose={() => setDismissedLibraries(prev => ({ ...prev, [check.key]: true }))}
                    className="mb-0 rounded-0 d-flex align-items-center"
                >
                    <AlertTriangle size={16} className="me-2 flex-shrink-0" />
                    <span>
                        {check.message}{' '}
                        <Alert.Link as={Link} to={check.settingsLink}>
                            {check.linkText}
                        </Alert.Link>
                    </span>
                </Alert>
            ))}
        </>
    );
};

export default SetupWarningBanner;
